import { Capacitor } from "@capacitor/core";
import { createLogger } from "./logger";

const logger = createLogger("CapacitorSafety");

/**
 * Capacitor Safety Utilities
 * Provides safe access to browser APIs that may not be available or work differently in Capacitor
 */

/**
 * Safely get window.location.origin
 * Falls back to production URL if unavailable
 * @returns {string} Origin URL
 */
export const getSafeOrigin = () => {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      return window.location.origin;
    }
    
    // Fallback for Capacitor apps
    if (Capacitor.isNativePlatform()) {
      return import.meta.env.VITE_PRODUCTION_URL || 'https://panichefs.com';
    }
    
    return 'https://panichefs.com';
  } catch (e) {
    logger.error('Failed to get window.location.origin:', e);
    return 'https://panichefs.com';
  }
};

/**
 * Safely get window.location.href
 * @returns {string} Current URL
 */
export const getSafeHref = () => {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.href) {
      return window.location.href;
    }
    return 'Unknown';
  } catch (e) {
    logger.error('Failed to get window.location.href:', e);
    return 'Unable to access location';
  }
};

/**
 * Safely get window.location.pathname
 * @returns {string} Current pathname
 */
export const getSafePathname = () => {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.pathname) {
      return window.location.pathname;
    }
    return '/';
  } catch (e) {
    logger.error('Failed to get window.location.pathname:', e);
    return '/';
  }
};

/**
 * Safely reload the page
 * @returns {boolean} True if reload was successful
 */
export const safeReload = () => {
  try {
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
      return true;
    }
    return false;
  } catch (e) {
    logger.error('Failed to reload page:', e);
    return false;
  }
};

/**
 * Safely navigate to a URL
 * @param {string} url - URL to navigate to
 * @returns {boolean} True if navigation was successful
 */
export const safeNavigate = (url) => {
  try {
    if (typeof window !== 'undefined' && window.location) {
      window.location.href = url;
      return true;
    }
    return false;
  } catch (e) {
    logger.error('Failed to navigate:', e);
    return false;
  }
};

/**
 * Safely extract coordinates from Google Maps geometry object
 * Handles both LatLng objects (with methods) and plain objects (with properties)
 * @param {Object} geometry - Google Maps geometry object
 * @returns {Object|null} Coordinates {lat, lng} or null if invalid
 */
export const extractCoordinates = (geometry) => {
  try {
    if (!geometry || !geometry.location) {
      logger.warn('Invalid geometry object:', geometry);
      return null;
    }

    const location = geometry.location;

    // Check if location has lat/lng as methods (Google Maps LatLng object)
    if (typeof location.lat === 'function' && typeof location.lng === 'function') {
      return {
        lat: location.lat(),
        lng: location.lng()
      };
    }

    // Check if location has lat/lng as properties (plain object from Geocoding API)
    if (typeof location.lat === 'number' && typeof location.lng === 'number') {
      return {
        lat: location.lat,
        lng: location.lng
      };
    }

    logger.warn('Unknown location format:', location);
    return null;
  } catch (e) {
    logger.error('Failed to extract coordinates:', e);
    return null;
  }
};

/**
 * Check if we're running in a Capacitor native environment
 * @returns {boolean} True if running in native app
 */
export const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
};

/**
 * Check if we're running in iOS
 * @returns {boolean} True if running on iOS
 */
export const isIOS = () => {
  try {
    return Capacitor.getPlatform() === 'ios';
  } catch (e) {
    return false;
  }
};

/**
 * Check if we're running in Android
 * @returns {boolean} True if running on Android
 */
export const isAndroid = () => {
  try {
    return Capacitor.getPlatform() === 'android';
  } catch (e) {
    return false;
  }
};

/**
 * Check if we're running in a web browser
 * @returns {boolean} True if running in browser
 */
export const isWeb = () => {
  try {
    return Capacitor.getPlatform() === 'web';
  } catch (e) {
    return true; // Assume web if Capacitor is not available
  }
};

/**
 * Get user agent safely
 * @returns {string} User agent string
 */
export const getSafeUserAgent = () => {
  try {
    return navigator.userAgent || 'Unknown';
  } catch (e) {
    logger.error('Failed to get user agent:', e);
    return 'Unknown';
  }
};

/**
 * Get screen dimensions safely
 * @returns {Object} Screen dimensions {width, height}
 */
export const getSafeScreenDimensions = () => {
  try {
    if (window.screen) {
      return {
        width: window.screen.width,
        height: window.screen.height
      };
    }
    return { width: 0, height: 0 };
  } catch (e) {
    logger.error('Failed to get screen dimensions:', e);
    return { width: 0, height: 0 };
  }
};

/**
 * Get viewport dimensions safely
 * @returns {Object} Viewport dimensions {width, height}
 */
export const getSafeViewportDimensions = () => {
  try {
    if (typeof window !== 'undefined') {
      return {
        width: window.innerWidth,
        height: window.innerHeight
      };
    }
    return { width: 0, height: 0 };
  } catch (e) {
    logger.error('Failed to get viewport dimensions:', e);
    return { width: 0, height: 0 };
  }
};

/**
 * Check if Redux DevTools is available
 * @returns {boolean} True if Redux DevTools extension is available
 */
export const hasReduxDevTools = () => {
  try {
    return (
      process.env.NODE_ENV === 'development' &&
      typeof window !== 'undefined' &&
      typeof window.__REDUX_DEVTOOLS_EXTENSION__ !== 'undefined'
    );
  } catch (e) {
    return false;
  }
};

/**
 * Get Redux DevTools extension safely
 * @returns {Function|null} Redux DevTools extension or null
 */
export const getReduxDevTools = () => {
  try {
    if (hasReduxDevTools()) {
      return window.__REDUX_DEVTOOLS_EXTENSION__();
    }
    return null;
  } catch (e) {
    logger.error('Failed to initialize Redux DevTools:', e);
    return null;
  }
};

const capacitorSafety = {
  getSafeOrigin,
  getSafeHref,
  getSafePathname,
  safeReload,
  safeNavigate,
  extractCoordinates,
  isNative,
  isIOS,
  isAndroid,
  isWeb,
  getSafeUserAgent,
  getSafeScreenDimensions,
  getSafeViewportDimensions,
  hasReduxDevTools,
  getReduxDevTools
};

export default capacitorSafety;
